/**
 * Workbench session draft helpers — crash/leave recovery for unfinished work.
 * Draft is stored in sessionStorage under `workbench_draft`.
 */

export const WORKBENCH_DRAFT_KEY = 'workbench_draft';
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface WorkbenchDraft {
    elements: any[];
    rootChain?: string | null;
    rootAddress?: string | null;
    selectedNodeId?: string | null;
    caseId?: string | null;
    investigationId?: string | null;
    savedAt?: number;
}

export function readWorkbenchDraft(): WorkbenchDraft | null {
    try {
        const raw = sessionStorage.getItem(WORKBENCH_DRAFT_KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw) as WorkbenchDraft;
        if (!draft?.savedAt || Date.now() - draft.savedAt >= DRAFT_MAX_AGE_MS) {
            sessionStorage.removeItem(WORKBENCH_DRAFT_KEY);
            return null;
        }
        if (!Array.isArray(draft.elements) || draft.elements.length === 0) {
            return null;
        }
        return draft;
    } catch {
        try { sessionStorage.removeItem(WORKBENCH_DRAFT_KEY); } catch { /* noop */ }
        return null;
    }
}

export function clearWorkbenchDraft(): void {
    try { sessionStorage.removeItem(WORKBENCH_DRAFT_KEY); } catch { /* noop */ }
}

/** True when a draft belongs to this case (or is legacy with no caseId). */
export function draftMatchesCase(draft: WorkbenchDraft, caseId: string): boolean {
    if (!draft.caseId) return true;
    return draft.caseId === caseId;
}

/**
 * Detect unfinished Workbench work for a case before navigating in.
 * Uses draft + persisted graph + session store case binding.
 */
export function hasUnfinishedWorkbenchForCase(
    caseId: string,
    opts: { canvasElementCount?: number; sessionCaseId?: string | null } = {},
): boolean {
    const draft = readWorkbenchDraft();
    if (draft && draftMatchesCase(draft, caseId)) return true;

    const sessionCaseId = opts.sessionCaseId ?? null;
    const canvasCount = opts.canvasElementCount ?? 0;
    if (sessionCaseId === caseId && canvasCount > 0) return true;

    return false;
}

/** Persisted live-investigation results of the current run (sessionStorage). */
export const WORKBENCH_RESULTS_KEY = 'workbench-results';
/** Persisted camera and pinned node positions of the current canvas (sessionStorage). */
export const WORKBENCH_VIEW_KEY = 'workbench-view';

/** The five keys the quota fallback has always cleared. */
export const WORKBENCH_CORE_SESSION_KEYS = [
    'workbench-transactions',
    'workbench-graph',
    'workbench-stash',
    WORKBENCH_DRAFT_KEY,
    'workbench-session',
] as const;

/** Every sessionStorage key the Workbench writes. One list for the quota fallback and "Leave without saving". */
export const WORKBENCH_SESSION_KEYS = [
    ...WORKBENCH_CORE_SESSION_KEYS,
    WORKBENCH_RESULTS_KEY,
    WORKBENCH_VIEW_KEY,
] as const;

/** Remove every Workbench key from sessionStorage. Best effort; never throws. */
export function removeWorkbenchSessionKeys(): void {
    for (const key of WORKBENCH_SESSION_KEYS) {
        try { sessionStorage.removeItem(key); } catch { /* noop */ }
    }
}

/** Write the unfinished-work draft. Returns false when storage refused it. */
export function writeWorkbenchDraft(draft: Omit<WorkbenchDraft, 'savedAt'>): boolean {
    try {
        sessionStorage.setItem(WORKBENCH_DRAFT_KEY, JSON.stringify({ ...draft, savedAt: Date.now() }));
        return true;
    } catch {
        return false;
    }
}

/**
 * Whether the page, unmounting by SPA navigation, should leave a draft behind:
 * only when it held unsaved work and the officer neither saved it nor chose to discard it.
 */
export function shouldWriteDraftOnLeave(hadUnsavedWork: boolean, leaveHandled: boolean): boolean {
    return hadUnsavedWork && !leaveHandled;
}
